import * as Sentry from '@sentry/nextjs'

import { ClipTimeRange } from '../types/types'

const START_FORMAT: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: 'numeric',
  minute: '2-digit',
  second: '2-digit',
}

const END_FORMAT: Intl.DateTimeFormatOptions = {
  hour: 'numeric',
  minute: '2-digit',
  second: '2-digit',
}

/**
 * Formats a clip's wall-clock range for the incident player caption, with the date in the locale's
 * numeric pattern, e.g. "07/15/2026, 2:04:10 PM – 2:06:40 PM" (en-US) or "15.07.2026, 14:04:10 –
 * 14:06:40" (de-DE). The date is shown once on the start; clips are short so the end reuses the
 * same day. Times render in the viewer's local timezone and locale conventions (`locale` is a test
 * seam — production callers omit it to use the browser locale). Returns `''` when the range is
 * missing or its timestamps are unparseable.
 */
export const formatClipRange = (
  range?: ClipTimeRange | null,
  locale?: string
): string => {
  if (!range?.startTime || !range?.endTime) {
    return ''
  }

  const start = new Date(range.startTime)
  const end = new Date(range.endTime)

  if (isNaN(start.getTime()) || isNaN(end.getTime())) {
    Sentry.captureException(
      new Error(
        `formatClipRange received unparseable clip timestamps: "${range.startTime}" – "${range.endTime}"`
      ),
      { tags: { feature: 'video_player' } }
    )
    return ''
  }

  const startLabel = start.toLocaleString(locale, START_FORMAT)
  const endLabel = end.toLocaleTimeString(locale, END_FORMAT)

  return `${startLabel} – ${endLabel}`
}
